import * as React from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from 'convex/react';
import { api } from '@/convex';
import type { Id } from '@/convex';
import { useClerkUser } from './useClerkUser';
import { useOrgContext } from './useOrgContext';

type ProjectKind = 'repo' | 'package' | 'design-system';

type GovernanceCiStatus =
  | 'not_configured'
  | 'pending_pr'
  | 'active'
  | 'broken'
  | 'degraded'
  | 'disabled';

interface ProjectSummary {
  _id: Id<'projects'>;
  name: string;
  slug: string;
  kind?: ProjectKind;
  repoFullName?: string;
  repoUrl?: string;
  defaultBranch?: string;
  governanceCiStatus?: GovernanceCiStatus;
  governanceCiLastSeenAt?: number;
  governanceCiPrUrl?: string;
  governanceLastError?: string;
  governanceLastCheckRunUrl?: string;
  governanceLastDetectionAt?: number;
}

export const ACTIVE_PROJECT_STORAGE_KEY = 'fragments-cloud:activeProjectSlug';

/**
 * Resolve the default project from a list — prefers a previously
 * selected slug (localStorage), then design-system kind, then first
 * project. Single source of truth for default project resolution.
 */
export function resolveDefaultProject<
  T extends { kind?: string; slug?: string },
>(projects: T[], savedSlug?: string | null): T | undefined {
  if (savedSlug) {
    const saved = projects.find((p) => p.slug === savedSlug);
    if (saved) return saved;
  }
  return projects.find((p) => p.kind === 'design-system') ?? projects[0];
}

interface ProjectFilterState {
  isAllProjects: boolean;
  selectedProjectId: Id<'projects'> | undefined;
  selectedProject: ProjectSummary | undefined;
  resolvedSlug: string | undefined;
  projects: ProjectSummary[];
  setProject: (slug: string) => void;
  isLoading: boolean;
}

export function useProjectContext(): ProjectFilterState {
  const { orgId, isLoading: orgLoading } = useOrgContext();
  const { user } = useClerkUser();
  const navigate = useNavigate();

  let searchProject: string | undefined;
  try {
    const search = useSearch({ strict: false }) as { project?: string };
    searchProject = search?.project;
  } catch {
    // Ignore missing route search context
  }

  const savedSlug = React.useMemo(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(ACTIVE_PROJECT_STORAGE_KEY);
  }, []);

  const shouldLoadProjects = !orgLoading && !!orgId;
  const projects = useQuery(
    api.projects.listByOrg,
    shouldLoadProjects ? { orgId, clerkUserId: user?.id } : 'skip',
  );

  const projectList = React.useMemo(
    () => (projects ?? []) as ProjectSummary[],
    [projects],
  );

  const projectMap = React.useMemo(() => {
    return new Map(projectList.map((p) => [p.slug, p]));
  }, [projectList]);

  const defaultProject = React.useMemo(
    () => resolveDefaultProject(projectList, savedSlug),
    [projectList, savedSlug],
  );

  const isAllProjects = !searchProject;
  const selectedProject = searchProject
    ? (projectMap.get(searchProject) ?? defaultProject)
    : defaultProject;

  const setProject = React.useCallback(
    (slug: string) => {
      if (slug !== 'all' && typeof window !== 'undefined') {
        localStorage.setItem(ACTIVE_PROJECT_STORAGE_KEY, slug);
      }
      navigate({
        search: (prev: Record<string, string>) => {
          if (slug === 'all') {
            const { project: _project, ...rest } = prev;
            return rest;
          }
          return { ...prev, project: slug };
        },
      } as Parameters<typeof navigate>[0]);
    },
    [navigate],
  );

  return {
    isAllProjects,
    selectedProjectId: selectedProject?._id,
    selectedProject,
    resolvedSlug: selectedProject?.slug,
    projects: projectList,
    setProject,
    isLoading: orgLoading || (shouldLoadProjects && projects === undefined),
  };
}
